/**
 * A paycheck in parts (TMMAPP-0173 Part A; TMMAPI-0202, MM-D-153): the sentences an occurrence's
 * served figures read in. tmm-api works out received, outstanding, extra and each part's share; this
 * only words them. The one subtraction is a closed-short part's "short by", the served
 * `amountMinor − receivedMinor` (spec item 1). Amounts read as magnitudes in "{x} of {y}"; an
 * outgoing occurrence (a negative amount) reads "paid" where an incoming one reads "received".
 */
import type { TFunction } from 'i18next';
import type { Occurrence } from '@/api/planned';
import type { LabelValues } from '@/components/statusLabel';
import { formatMoneyMinor } from '@/i18n/format';

/** Outgoing money: an expense's occurrence (its served amount is negative). */
export function isOutgoing(amountMinor: number | null | undefined): boolean {
  return amountMinor != null && amountMinor < 0;
}

/**
 * The occurrence state label's i18next context: an outgoing `PARTIAL` reads "Partly paid"
 * (`status.occurrence.PARTIAL_expense`), every other state as the registry words it.
 */
export function occurrenceLabelValues(
  amountMinor: number | null | undefined,
): LabelValues | undefined {
  return isOutgoing(amountMinor) ? { context: 'expense' } : undefined;
}

/** A part-paid occurrence's own block shows: `PARTIAL`, or closed short. */
export function isPartPaid(occurrence: Occurrence): boolean {
  return (
    occurrence.receivedMinor !== null && (occurrence.state === 'PARTIAL' || occurrence.closedShort)
  );
}

function money(minor: number, currency: string | null, locale: string): string {
  return formatMoneyMinor(Math.abs(minor), currency, locale);
}

/** "{received} of {amount} received" (or "paid"), or "Closed at {received} of {amount}". */
export function progressLine(
  t: TFunction,
  occurrence: Occurrence,
  currency: string | null,
  locale: string,
): string | null {
  if (occurrence.receivedMinor === null) return null;
  const values = {
    received: money(occurrence.receivedMinor, currency, locale),
    amount: money(occurrence.amountMinor, currency, locale),
  };
  if (occurrence.closedShort) return t('planned.parts.closedAt', values);
  return isOutgoing(occurrence.amountMinor)
    ? t('planned.parts.progressPaid', values)
    : t('planned.parts.progressReceived', values);
}

/** "{outstanding} still to come", while the occurrence is open and tmm-api serves it. */
export function outstandingLine(
  t: TFunction,
  occurrence: Occurrence,
  currency: string | null,
  locale: string,
): string | null {
  if (occurrence.closedShort || occurrence.state !== 'PARTIAL') return null;
  if (occurrence.outstandingMinor === null) return null;
  return t('planned.parts.stillToCome', {
    outstanding: money(occurrence.outstandingMinor, currency, locale),
  });
}

/** "{extra} more than planned isn't allocated", only when tmm-api serves `extraMinor`. */
export function extraLine(
  t: TFunction,
  occurrence: Occurrence,
  currency: string | null,
  locale: string,
): string | null {
  if (occurrence.extraMinor === null) return null;
  return t('planned.parts.extra', { extra: money(occurrence.extraMinor, currency, locale) });
}

/** A part's "{received} of {amount}"; null when it holds its whole amount (a full paycheck). */
export function partLine(
  t: TFunction,
  part: { amountMinor: number; receivedMinor: number | null },
  currency: string | null,
  locale: string,
): string | null {
  if (part.receivedMinor === null || part.receivedMinor === part.amountMinor) return null;
  return t('planned.parts.partOf', {
    received: money(part.receivedMinor, currency, locale),
    amount: money(part.amountMinor, currency, locale),
  });
}

/** A closed-short part's "short by {difference}": the served `amountMinor − receivedMinor`. */
export function shortByLine(
  t: TFunction,
  part: { amountMinor: number; receivedMinor: number | null },
  currency: string | null,
  locale: string,
): string | null {
  if (part.receivedMinor === null || part.receivedMinor === part.amountMinor) return null;
  return t('planned.parts.shortBy', {
    difference: money(part.amountMinor - part.receivedMinor, currency, locale),
  });
}

/** A bank line's "{amount} on {date}" (a Remove button's name, a suggestion). */
export function lineAmount(minor: number, currency: string | null, locale: string): string {
  return money(minor, currency, locale);
}

/** "That's all"'s confirmation title: "Close this paycheck at {received}?". */
export function closeTitle(
  t: TFunction,
  occurrence: Occurrence,
  currency: string | null,
  locale: string,
): string {
  const received = money(occurrence.receivedMinor ?? 0, currency, locale);
  return isOutgoing(occurrence.amountMinor)
    ? t('planned.parts.closeTitlePaid', { received })
    : t('planned.parts.closeTitle', { received });
}
